import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, ShieldAlert, type LucideIcon } from 'lucide-react';
import { cn } from '@shared/lib/cn';
import { Button } from './Button';
import { Icon } from './Icon';

export interface BannerProps {
  tone: 'warning' | 'info' | 'danger';
  icon: LucideIcon;
  children: ReactNode;
  action?: ReactNode;
  className?: string;
}

const TONES = {
  warning: 'bg-warning-subtle text-warning-text border-warning',
  info: 'bg-info-subtle text-info-text border-info',
  danger: 'bg-danger-subtle text-danger-text border-danger',
} as const;

/** Full-width persistent banner under the top bar (MFA enrolment, impersonation, offline). */
export function Banner({ tone, icon, children, action, className }: BannerProps) {
  return (
    <div role="status" className={cn('flex flex-wrap items-center gap-3 border-b px-page-x py-2 text-sm lg:px-8', TONES[tone], className)}>
      <Icon icon={icon} size={18} />
      <div className="min-w-0 flex-1">{children}</div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export interface ImpersonationBannerProps {
  subjectName: string;
  actorName: string;
  onExit: () => void;
  exiting?: boolean;
}

/** "Viewing as" banner in the warning colour (UX doc SA-04: impersonate with audit). */
export function ImpersonationBanner({ subjectName, actorName, onExit, exiting }: ImpersonationBannerProps) {
  const { t } = useTranslation('common');
  return (
    <Banner
      tone="warning"
      icon={Eye}
      action={
        <Button size="sm" variant="secondary" onClick={onExit} loading={exiting} leadingIcon={ShieldAlert}>
          {t('impersonation.exit')}
        </Button>
      }
    >
      <strong className="font-semibold">{t('impersonation.title', { subject: subjectName })}</strong>{' '}
      <span>{t('impersonation.body', { actor: actorName })}</span>
    </Banner>
  );
}
